import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, Trash2, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminPhotos() {
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState("");
  const [product, setProduct] = useState(null);
  const [progress, setProgress] = useState(null);
  const [altDraft, setAltDraft] = useState({});
  const libInput = useRef(null);
  const camInput = useRef(null);

  useEffect(() => {
    api.get("/admin/products").then((r) => {
      setProducts(r.data);
      if (r.data.length) setSelected(r.data[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const load = (id) => {
    if (!id) return;
    api.get(`/admin/products/${id}`).then((r) => {
      setProduct(r.data);
      const drafts = {};
      (r.data.images || []).forEach((img) => { drafts[img.image_id] = img.alt || ""; });
      setAltDraft(drafts);
    }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(selected); }, [selected]);

  const upload = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("alt", "");
    setProgress(0);
    api.post(`/admin/products/${selected}/images`, form, {
      onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 50),
    }).then(() => { toast.success("Photo uploaded."); load(selected); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setProgress(null));
  };

  const reorder = (imageId, dir) => {
    const ids = product.images.map((i) => i.image_id);
    const idx = ids.indexOf(imageId);
    const swap = idx + dir;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    api.post(`/admin/products/${selected}/images/reorder`, { image_ids: ids })
      .then(() => { toast.success(ids[0] === imageId ? "Main photo updated." : "Order updated."); load(selected); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const saveAlt = (imageId) => {
    api.put(`/admin/products/${selected}/images/${imageId}`, { alt: altDraft[imageId] || "" })
      .then(() => toast.success("Alt text saved."))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const remove = (imageId) => {
    if (!window.confirm("Remove this photo? This cannot be undone.")) return;
    api.delete(`/admin/products/${selected}/images/${imageId}`)
      .then(() => { toast.success("Photo removed."); load(selected); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const hasDemo = (product?.images || []).some((i) => i.is_demo);

  return (
    <div data-testid="admin-photos-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Photos</h1>
      <p className="mt-1 text-sm text-cream-muted">Pick a product, then add or manage its photos. The first photo is the main photo shown in the shop.</p>

      <label htmlFor="photo-product" className="mt-5 block text-[11px] font-bold uppercase tracking-widest text-cream-muted">Product</label>
      <select id="photo-product" value={selected} onChange={(e) => setSelected(e.target.value)}
        className="mt-1 w-full max-w-md rounded-xl border border-choco-line bg-choco px-4 py-3 text-sm text-cream-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
        data-testid="photo-product-select">
        {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {product && (
        <>
          {hasDemo && (
            <p className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="demo-image-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              This product uses demonstration imagery. Replace it with real photos before publishing.
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => libInput.current?.click()} className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco" data-testid="photo-upload-library">
              <ImagePlus className="h-4 w-4" aria-hidden="true" /> Choose from library
            </button>
            <button onClick={() => camInput.current?.click()} className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="photo-upload-camera">
              <Camera className="h-4 w-4" aria-hidden="true" /> Take a photo
            </button>
            <input ref={libInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" aria-hidden="true" data-testid="photo-file-library"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            <input ref={camInput} type="file" accept="image/*" capture="environment" className="hidden" aria-hidden="true" data-testid="photo-file-camera"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </div>

          {progress !== null && (
            <div className="mt-4" role="status" data-testid="photo-upload-progress">
              <div className="h-2.5 w-full max-w-md overflow-hidden rounded-full bg-choco">
                <div className="h-full rounded-full bg-gold transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-1 text-xs text-cream-muted">Uploading… {progress}%</p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="photo-list">
            {(product.images || []).map((img, idx) => (
              <div key={img.image_id} className="rounded-3xl border border-choco-line bg-choco-light p-4" data-testid={`photo-card-${img.image_id}`}>
                <div className="relative">
                  <img src={img.url_400 || img.url} alt={img.alt || product.name} className="aspect-[4/3] w-full rounded-2xl object-cover" />
                  {idx === 0 && <span className="absolute left-2 top-2 rounded-full bg-gold px-2.5 py-0.5 text-[11px] font-bold text-choco" data-testid={`photo-main-${img.image_id}`}>Main photo</span>}
                  {img.is_demo && <span className="absolute right-2 top-2 rounded-full bg-amber-500/90 px-2.5 py-0.5 text-[11px] font-bold text-choco">Demo</span>}
                </div>
                <label htmlFor={`alt-${img.image_id}`} className="mt-3 block text-[11px] font-bold uppercase tracking-widest text-cream-muted">
                  Alt text (describes the photo for screen readers)
                </label>
                <div className="mt-1 flex gap-2">
                  <input id={`alt-${img.image_id}`} value={altDraft[img.image_id] ?? ""} onChange={(e) => setAltDraft({ ...altDraft, [img.image_id]: e.target.value })}
                    className="min-w-0 flex-1 rounded-xl border border-choco-line bg-choco px-3 py-2.5 text-sm text-cream-text" data-testid={`photo-alt-${img.image_id}`} />
                  <button onClick={() => saveAlt(img.image_id)} className="rounded-xl border border-choco-line px-3 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`photo-alt-save-${img.image_id}`}>Save</button>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <button onClick={() => reorder(img.image_id, -1)} disabled={idx === 0} aria-label="Move earlier" className="rounded-lg border border-choco-line p-2.5 text-cream-muted hover:text-gold disabled:opacity-30" data-testid={`photo-up-${img.image_id}`}><ArrowUp className="h-4 w-4" /></button>
                    <button onClick={() => reorder(img.image_id, 1)} disabled={idx === product.images.length - 1} aria-label="Move later" className="rounded-lg border border-choco-line p-2.5 text-cream-muted hover:text-gold disabled:opacity-30" data-testid={`photo-down-${img.image_id}`}><ArrowDown className="h-4 w-4" /></button>
                  </div>
                  <button onClick={() => remove(img.image_id)} className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 px-3 py-2 text-xs font-bold text-red-300 hover:bg-red-500/10" data-testid={`photo-remove-${img.image_id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Remove
                  </button>
                </div>
              </div>
            ))}
            {product.images?.length === 0 && (
              <p className="rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="photo-empty">No photos yet — add the first one above.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
